import React, { useEffect, useState } from 'react';
import { Editor, EditorTools, ProseMirror } from '@progress/kendo-react-editor';
import { defaultMarkdownParser, defaultMarkdownSerializer } from 'prosemirror-markdown';
// Styles
import './style.scss';
import { theme } from 'src/theme';

if (theme.mode === 'dark') {
  import(/* webpackChunkName: "dark" */ './style.scss?dark');
}

const createView = ({ dom, viewProps }) => {
  const state = ProseMirror.EditorState.create({
    doc: viewProps.state.doc,
    plugins: [
      ProseMirror.keymap({
        'Mod-u': () => true,
      }),
      ...viewProps.state.plugins,
    ],
  });
  return new ProseMirror.EditorView(
    { mount: dom },
    { ...viewProps, state },
  );
};

const {
  Bold,
  Italic,
  Link,
  FormatBlock,
  Undo,
  Redo,
  OrderedList,
  UnorderedList,
} = EditorTools;

/**
 * A markdown textarea component.
 *
 * @typedef {Object} MarkdownProps
 * @property {string} [value] Represents the value of the component.
 * @property {Function} onChange A callback when the value is changed.
 * @property {number} [height={125}] The height of the component. 125 by default.
 * @property {string} [dataTestId] Data test Id.
 * @property {boolean} [allowLinks={true}] Indicates if it is possible to insert links into the text.
 * @property {boolean} [disabled={false}] Indicates if the component is disabled. False by default.
 *
 * @param {MarkdownProps} props Component props.
 * @returns {React.ReactElement} Markdown component
 */
const MarkdownTextarea = ({ value, onChange, height = 125, disabled, dataTestId, allowLinks = true, ...rest }) => {
  // Markdown object value
  const [mdValue, setMdValue] = useState(defaultMarkdownParser.parse(value ?? ''));

  const handleChange = (e) => {
    const { value: newMDValue } = e;
    const text = defaultMarkdownSerializer.serialize(newMDValue);

    // Ignore pasted images

    // Ignore pasted Blob links
    if (text.includes('](blob:')) {
      // Clean text from images
      const cleaned = text.replaceAll(/!?\[.*?\]\(blob:.*?\)/gm, '');
      const newMDValue = defaultMarkdownParser.parse(cleaned);
      setMdValue(newMDValue);
      return;
    }

    // MD image with base64 string data
    if (text && /!\[.*\]\(data:image\/.*;base64,.*\)/gm.test(text)) {
      // Clean text from images
      const cleaned = text.replaceAll(/!\[.*?\]\(data:image\/.*?\)/gm, '');
      const newMDValue = defaultMarkdownParser.parse(cleaned);
      setMdValue(newMDValue);
      return;
    }

    // Ignore pasted MD image links (marked as !) - handled by onPaste
    if (/!\[.*\]\(https?:\/\/.*\)/gm.test(text)) {
      // Clean text from images
      const cleaned = text.replaceAll(/\[!\[.*?\]\(https?:\/\/.*?\)\]\(.*?\)/gm, '');
      const cleaned2 = cleaned.replaceAll(/!\[.*?\]\(https?:\/\/.*?\)/gm, '');
      const newMDValue = defaultMarkdownParser.parse(cleaned2);
      setMdValue(newMDValue);
      return;
    }

    // HTML img tag with base64 string data
    const startImgTag = '<img src="data:image/';

    if (text && text.includes(startImgTag)) {
      return;
    }

    setMdValue(newMDValue);
  };

  // Send changes to the client
  useEffect(() => {
    const textValue = defaultMarkdownSerializer.serialize(mdValue);
    onChange({ value: textValue });
  }, [mdValue]);

  return (
    <div className='markdown-textarea' data-testid={dataTestId}>
      <Editor
        contentStyle={{ height }}
        defaultEditMode='div'
        onMount={createView}
        tools={[
          allowLinks ? [Bold, Italic, Link] : [Bold, Italic],
          [Undo, Redo],
          [OrderedList, UnorderedList],
          FormatBlock,
        ]}
        value={mdValue}
        onChange={handleChange}
        disabled={disabled}
        {...rest}
      />
    </div>
  );
};

export default MarkdownTextarea;
